import { ReactNode, useState, useCallback, useRef, useEffect } from "react"
import { router, usePage } from "@inertiajs/react"
import ChessScreen from "@/components/game/ChessScreen"
import { GameProvider } from "@/hooks/useGame"
import { GameMovesProvider, useGameMoves } from "@/hooks/useGameMoves"
import { useGuestGame } from "@/hooks/useGuestGame"
import SignupModal from "@/components/guest/SignupModal"
import AppLayout from "@/layouts/AppLayout"
import { GameShowProps, MovesHistory } from "@/types/types"
import { captureEvent } from "@/lib/posthog"

function GuestMoveTracker() {
  const { movesHistory } = useGameMoves()
  const firstMoveTracked = useRef(false)

  useEffect(() => {
    if (!firstMoveTracked.current && movesHistory.length > 0 && movesHistory[0]?.length > 0) {
      firstMoveTracked.current = true
      captureEvent("guest_move_made", { move_number: 1 })
    }
  }, [movesHistory])

  return null
}

function GuestSignupModal({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean
  onClose: () => void
  onSuccess: (moves: MovesHistory) => void
}) {
  const { movesHistory } = useGameMoves()

  return (
    <SignupModal
      isOpen={isOpen}
      onClose={onClose}
      onSuccess={() => onSuccess(movesHistory)}
    />
  )
}

export default function TryShow() {
  const { game } = usePage<GameShowProps>().props
  const { initialGame, onMovesChange, clearGame, saveToServer } = useGuestGame()
  const [boardKey, setBoardKey] = useState(0)
  const [showSignup, setShowSignup] = useState(false)

  const handleClear = useCallback(() => {
    clearGame()
    setBoardKey((k) => k + 1)
  }, [clearGame])

  const handleSignupSuccess = useCallback(async (movesHistory: MovesHistory) => {
    setShowSignup(false)
    const gameId = await saveToServer(movesHistory)
    if (gameId) {
      captureEvent("guest_game_saved")
      router.visit(`/games/${gameId}`)
    }
  }, [saveToServer])

  return (
    <GameMovesProvider
      key={boardKey}
      mode="draft"
      initialGame={initialGame}
      onMovesChange={onMovesChange}
    >
      <GameProvider game={game}>
        <ChessScreen
          isOwner={false}
          guestActions={{
            onSave: () => setShowSignup(true),
            onClear: handleClear,
          }}
        />
        <GuestMoveTracker />
        <GuestSignupModal
          isOpen={showSignup}
          onClose={() => setShowSignup(false)}
          onSuccess={handleSignupSuccess}
        />
      </GameProvider>
    </GameMovesProvider>
  )
}

TryShow.layout = (page: ReactNode) => (
  <AppLayout hideTabs hideBanners guestMode>
    {page}
  </AppLayout>
)
